import React from "react";
import AdminLayout from "@/components/AdminLayout";
import CmsResource from "@/components/CmsResource";
import { API, TOKEN_KEY, formatPrice } from "@/api/client";
import { toast } from "sonner";
import { Instagram } from "lucide-react";

async function downloadInstagram(slug) {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API}/admin/properties/${slug}/instagram.png`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("failed");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `instagram-${slug}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Imagem gerada.");
  } catch {
    toast.error("Erro a gerar imagem.");
  }
}

const fields = [
  { name: "title", label: "Título", type: "text", wide: true },
  { name: "slug", label: "Slug (URL)", type: "text", help: "Ex: t2-baixa-porto (sem espaços)" },
  { name: "reference", label: "Referência", type: "text", help: "Ex: 86491282 (código externo ou interno)" },
  { name: "status", label: "Estado", type: "select", options: ["à venda", "reservado", "vendido"] },
  { name: "order", label: "Ordem", type: "number", help: "0 aparece primeiro (dentro do mesmo estado)." },
  { name: "price", label: "Preço (€)", type: "number" },
  { name: "currency", label: "Moeda", type: "text" },
  { name: "locality", label: "Localidade", type: "text" },
  { name: "zone_slug", label: "Zona (slug)", type: "select", options: [
    { value: "porto", label: "Porto" },
    { value: "gondomar", label: "Gondomar" },
    { value: "maia", label: "Maia" },
    { value: "ermesinde", label: "Ermesinde" },
    { value: "valongo", label: "Valongo" },
  ]},
  { name: "property_type", label: "Tipo", type: "select", options: ["Apartamento", "Moradia", "Terreno", "Loja/Comercial"] },
  { name: "typology", label: "Tipologia", type: "select", options: ["T0", "T1", "T2", "T3", "T4", "T5+"] },
  { name: "area", label: "Área (m²)", type: "number" },
  { name: "short_description", label: "Descrição curta", type: "textarea", wide: true, rows: 2 },
  { name: "full_description", label: "Descrição completa", type: "textarea", wide: true, rows: 6 },
  { name: "features", label: "Características", type: "list", wide: true },
  { name: "main_image", label: "Imagem principal", type: "image", wide: true },
  { name: "gallery", label: "Galeria", type: "image-list", wide: true },
  { name: "video_url", label: "Vídeo (URL do YouTube ou Vimeo)", type: "text", wide: true, placeholder: "https://www.youtube.com/watch?v=..." },
  { name: "drone_url", label: "Vídeo aéreo / Street View (URL)", type: "text", wide: true, help: "YouTube/Vimeo (drone) ou link 'Incorporar mapa' do Google Maps/Street View", placeholder: "https://www.google.com/maps/embed?pb=... ou https://youtu.be/..." },
  { name: "featured", label: "Em destaque?", type: "checkbox", checkboxLabel: "Mostrar em destaque na homepage" },
  { name: "latitude", label: "Latitude", type: "number" },
  { name: "longitude", label: "Longitude", type: "number" },
  { name: "cta_label", label: "CTA — texto", type: "text" },
  { name: "cta_url", label: "CTA — link", type: "text" },
];

const defaults = {
  title: "", slug: "", reference: "", status: "à venda", order: 0, price: 0, currency: "EUR", locality: "", zone_slug: "porto",
  property_type: "Apartamento", typology: "T1", area: 0, short_description: "", full_description: "",
  features: [], main_image: "", gallery: [], featured: false, latitude: null, longitude: null,
  cta_label: "Marcar visita", cta_url: "/contacto", video_url: "", drone_url: "",
};

export default function AdminProperties() {
  return (
    <AdminLayout>
      <CmsResource
        resource="properties"
        title="Imóveis"
        singular="Imóvel"
        fields={fields}
        defaults={defaults}
        displayColumns={[
          { key: "title", label: "Título" },
          { key: "order", label: "Ordem" },
          { key: "zone_slug", label: "Zona" },
          { key: "typology", label: "Tipologia" },
          { key: "price", label: "Preço", render: (r) => formatPrice(r.price, r.currency) },
          { key: "status", label: "Estado" },
          { key: "featured", label: "Destaque", render: (r) => r.featured ? "Sim" : "—" },
          { key: "_ig", label: "Instagram", render: (r) => (
            <button type="button" onClick={(e) => { e.stopPropagation(); downloadInstagram(r.slug); }} className="btn-ghost" data-testid={`ig-download-${r.slug}`}>
              <Instagram className="h-3.5 w-3.5" /> Post
            </button>
          )},
        ]}
      />
    </AdminLayout>
  );
}
